import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { 
  Shield, 
  LayoutDashboard, 
  Users, 
  CreditCard,
  BarChart3,
  Settings,
  Upload,
  LogOut,
  Menu,
  X,
  User,
  Images,
  Layers,
  Globe,
  Activity,
  Mail
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminLayout = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) {
        navigate("/admin/login");
        return;
      }
      
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
    } catch (error) {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      localStorage.removeItem("admin_token");
      toast.success("Logged out successfully");
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  };

  const navItems = [
    { to: "/admin", icon: <LayoutDashboard className="w-5 h-5" />, label: "Overview", end: true },
    { to: "/admin/photographers", icon: <Users className="w-5 h-5" />, label: "Photographers" },
    { to: "/admin/activity-log", icon: <Activity className="w-5 h-5" />, label: "Activity Log" },
    { to: "/admin/tiers", icon: <Layers className="w-5 h-5" />, label: "Tiers & Features" },
    { to: "/admin/stripe", icon: <CreditCard className="w-5 h-5" />, label: "Stripe Connect" },
    { to: "/admin/galleries", icon: <Images className="w-5 h-5" />, label: "Galleries" },
    { to: "/admin/billing", icon: <CreditCard className="w-5 h-5" />, label: "Billing" },
    { to: "/admin/analytics", icon: <BarChart3 className="w-5 h-5" />, label: "Analytics" },
    { to: "/admin/system-emails", icon: <Mail className="w-5 h-5" />, label: "System Emails" },
    { to: "/admin/site-settings", icon: <Globe className="w-5 h-5" />, label: "Site Settings" },
    { to: "/admin/settings", icon: <Upload className="w-5 h-5" />, label: "Upload Settings" },
    { to: "/admin/profile", icon: <User className="w-5 h-5" />, label: "Profile" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="text-lg font-serif">360Lumina</span>
                <span className="text-xs text-primary block">ADMIN</span>
              </div>
            </div>
            <button 
              className="lg:hidden text-muted-foreground hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Admin user section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{admin?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{admin?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-white"
              onClick={handleLogout}
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 glass border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              className="lg:hidden text-muted-foreground hover:text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-mobile-menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-4 ml-auto">
              <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-white">
                View Site →
              </a>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
